<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { RouterLink, RouterView, useRoute } from 'vue-router';
import { useUserStore } from './stores/user';
import { useNetbirdStore } from './stores/netbird';
import NetbirdStatusBadge from './components/NetbirdStatusBadge.vue';
import logoWhite from './assets/brand/logo-white.svg';

const route = useRoute();
const user = useUserStore();
const netbird = useNetbirdStore();
const showHeader = computed(() => route.path !== '/login' && !!user.identity);
// Only show the offline warning once the user is past /login — pre-login,
// not having NetBird configured yet is the expected initial state.
const showNetbirdWarning = computed(() =>
    !!user.identity && route.path !== '/login' && netbird.status === 'offline',
);
// Distinguish "Chrome LNA permission denied" from generic offline so we
// can show actionable fix steps instead of telling the user to start the
// NetBird client they may already be running. See stores/netbird.ts.
const lnaBlocked = computed(() =>
    showNetbirdWarning.value && netbird.permissionState === 'denied',
);

const LNA_SETTINGS_URL = 'chrome://settings/content/localNetworkAccess';
const showLnaHelp = ref(false);
const lnaUrlCopied = ref(false);
async function copyLnaSettingsUrl() {
    try {
        await navigator.clipboard.writeText(LNA_SETTINGS_URL);
        lnaUrlCopied.value = true;
        setTimeout(() => { lnaUrlCopied.value = false; }, 2000);
    } catch { /* clipboard unavailable — user can still select+copy manually */ }
}

const menuOpen = ref(false);
function toggleMenu() { menuOpen.value = !menuOpen.value; }
function closeMenu() { menuOpen.value = false; }

// Close the overflow menu when the user navigates or taps outside.
function closeMenuOnOutside(e: MouseEvent) {
    const t = e.target as HTMLElement;
    if (!t.closest('[data-mobile-menu]')) menuOpen.value = false;
}
onMounted(() => { document.addEventListener('click', closeMenuOnOutside); });
onBeforeUnmount(() => { document.removeEventListener('click', closeMenuOnOutside); });

// Start NetBird reachability polling as soon as the user is authenticated;
// stop it on sign-out so we don't spin a probe interval in the background.
watch(() => !!user.identity, (authed) => {
    if (authed) netbird.startPolling();
    else netbird.stopPolling();
}, { immediate: true });
onBeforeUnmount(() => netbird.stopPolling());
</script>

<template>
    <div class="min-h-screen flex flex-col">
        <header v-if="showHeader" class="bg-bp-panel border-b border-bp-border px-4 sm:px-6 py-3">
            <div class="flex items-center gap-3 sm:gap-6">
                <RouterLink to="/sites" class="flex items-center gap-3 min-w-0">
                    <img :src="logoWhite" alt="Bazeport" class="h-6 w-auto flex-shrink-0" />
                    <span class="hidden md:inline text-bp-muted text-sm border-l border-bp-border pl-3 whitespace-nowrap">Support VPN</span>
                </RouterLink>

                <nav class="hidden sm:flex gap-4 text-sm text-bp-muted">
                    <RouterLink to="/sites" class="hover:text-bp-link-hover">Sites</RouterLink>
                    <RouterLink to="/connect" class="hover:text-bp-link-hover">Connect</RouterLink>
                    <RouterLink v-if="!user.identity?.isInternal"
                                to="/access"
                                class="hover:text-bp-link-hover whitespace-nowrap">My access</RouterLink>
                    <RouterLink v-if="user.identity?.isApprover"
                                to="/approvals"
                                class="hover:text-bp-link-hover whitespace-nowrap">Approvals</RouterLink>
                    <RouterLink v-if="user.identity?.isInternal"
                                to="/account/cli-tokens"
                                class="hover:text-bp-link-hover whitespace-nowrap">CLI tokens</RouterLink>
                    <a v-if="!user.identity?.isInternal"
                       href="https://auth.bazeport.net/account/passkeys"
                       class="hover:text-bp-link-hover whitespace-nowrap">Manage passkeys</a>
                </nav>

                <div class="ml-auto hidden sm:flex items-center gap-3 text-sm min-w-0">
                    <span class="text-bp-muted truncate max-w-[14rem]" :title="user.identity?.email">
                        {{ user.identity?.email }}
                    </span>
                    <NetbirdStatusBadge v-if="netbird.status === 'online'"
                                        :client-ip="netbird.clientIp" />
                    <span v-if="user.identity?.isInternal"
                          class="px-2 py-0.5 rounded bg-brand-bright-blue/25 text-brand-light-blue text-xs font-medium flex-shrink-0">Internal</span>
                    <button @click="user.logout()" class="text-bp-muted hover:text-bp-link-hover">Sign out</button>
                </div>

                <!-- Mobile overflow menu — visible only below sm. Holds the
                     nav + the identity / sign-out controls so the header
                     stays a single 48px row on phones. -->
                <div class="ml-auto sm:hidden relative" data-mobile-menu>
                    <button
                        type="button"
                        @click.stop="toggleMenu"
                        :aria-expanded="menuOpen"
                        aria-label="Menu"
                        class="p-2 -mr-2 rounded text-bp-muted hover:text-bp-text">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" stroke-width="2" stroke-linecap="round"
                             stroke-linejoin="round" class="w-5 h-5">
                            <line x1="3" y1="6" x2="21" y2="6" />
                            <line x1="3" y1="12" x2="21" y2="12" />
                            <line x1="3" y1="18" x2="21" y2="18" />
                        </svg>
                    </button>

                    <div
                        v-if="menuOpen"
                        class="absolute right-0 mt-2 w-64 bg-bp-panel border border-bp-border rounded
                               shadow-2xl z-20 py-2 text-sm">
                        <div class="px-4 pb-2 mb-2 border-b border-bp-border">
                            <p class="text-bp-text truncate" :title="user.identity?.email">
                                {{ user.identity?.email }}
                            </p>
                            <div class="flex items-center gap-2 mt-1">
                                <NetbirdStatusBadge v-if="netbird.status === 'online'"
                                                    :client-ip="netbird.clientIp" />
                                <span v-if="user.identity?.isInternal"
                                      class="text-xs text-brand-light-blue">Internal</span>
                            </div>
                        </div>
                        <RouterLink to="/sites" @click="closeMenu"
                                    class="block px-4 py-2 text-bp-muted hover:bg-bp-panel-2 hover:text-bp-text">
                            Sites
                        </RouterLink>
                        <RouterLink to="/connect" @click="closeMenu"
                                    class="block px-4 py-2 text-bp-muted hover:bg-bp-panel-2 hover:text-bp-text">
                            Connect
                        </RouterLink>
                        <RouterLink v-if="!user.identity?.isInternal"
                                    to="/access"
                                    @click="closeMenu"
                                    class="block px-4 py-2 text-bp-muted hover:bg-bp-panel-2 hover:text-bp-text">
                            My access
                        </RouterLink>
                        <RouterLink v-if="user.identity?.isApprover"
                                    to="/approvals"
                                    @click="closeMenu"
                                    class="block px-4 py-2 text-bp-muted hover:bg-bp-panel-2 hover:text-bp-text">
                            Approvals
                        </RouterLink>
                        <RouterLink v-if="user.identity?.isInternal"
                                    to="/account/cli-tokens"
                                    @click="closeMenu"
                                    class="block px-4 py-2 text-bp-muted hover:bg-bp-panel-2 hover:text-bp-text">
                            CLI tokens
                        </RouterLink>
                        <a v-if="!user.identity?.isInternal"
                           href="https://auth.bazeport.net/account/passkeys"
                           class="block px-4 py-2 text-bp-muted hover:bg-bp-panel-2 hover:text-bp-text">
                            Manage passkeys
                        </a>
                        <button
                            type="button"
                            @click="closeMenu(); user.logout()"
                            class="w-full text-left px-4 py-2 mt-1 border-t border-bp-border text-bp-muted hover:bg-bp-panel-2 hover:text-bp-text">
                            Sign out
                        </button>
                    </div>
                </div>
            </div>
        </header>

        <!-- Chrome LNA permission denied banner. Amber treatment (not red)
             because it's actionable — the user can fix this without help
             from support. Shown only when probe is failing AND the LNA
             permission state is explicitly 'denied'; for 'prompt' /
             'granted' / 'unsupported' / 'unknown' we fall through to the
             generic offline banner below. -->
        <div v-if="lnaBlocked"
             class="bg-amber-900/40 border-b border-amber-700 text-amber-100 px-4 sm:px-6 py-3">
            <div class="max-w-7xl mx-auto text-sm">
                <div class="flex items-start gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round"
                         stroke-linejoin="round" class="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-300">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <div class="flex-1 min-w-0">
                        <p class="font-medium text-amber-50">Chrome blocked local network access</p>
                        <p class="mt-0.5 text-amber-200">
                            We can't detect your NetBird connection because Chrome's local network
                            permission is denied for this site. This usually means the permission
                            prompt was dismissed earlier.
                        </p>
                        <button
                            type="button"
                            @click="showLnaHelp = !showLnaHelp"
                            class="mt-1 text-amber-300 underline hover:text-amber-100">
                            {{ showLnaHelp ? 'Hide fix instructions' : 'How to fix' }}
                        </button>
                        <div v-if="showLnaHelp" class="mt-3 text-amber-200">
                            <ol class="list-decimal pl-5 space-y-1.5">
                                <li>
                                    Copy this URL:
                                    <code class="bg-black/30 px-1.5 py-0.5 rounded text-amber-100 select-all">{{ LNA_SETTINGS_URL }}</code>
                                    <button
                                        type="button"
                                        @click="copyLnaSettingsUrl"
                                        class="ml-1 px-2 py-0.5 rounded border border-amber-700 text-xs hover:bg-amber-900/40">
                                        {{ lnaUrlCopied ? 'Copied' : 'Copy' }}
                                    </button>
                                </li>
                                <li>
                                    Paste it into Chrome's address bar and press Enter.
                                    <span class="text-amber-300">(Chrome blocks clicking links to <code>chrome://</code> URLs, which is why we can't link it directly.)</span>
                                </li>
                                <li>
                                    Find <code class="bg-black/30 px-1 rounded">supportvpn.bazeport.net</code>
                                    under <strong>Not allowed to access local network</strong> and click the trash icon to remove it.
                                </li>
                                <li>Reload this page. Click <strong>Allow</strong> when Chrome asks for local network access.</li>
                            </ol>
                        </div>
                    </div>
                    <button
                        type="button"
                        :disabled="netbird.checking"
                        @click="netbird.probe()"
                        class="flex-shrink-0 px-3 py-1 rounded border border-amber-700 text-amber-100
                               hover:bg-amber-900/40 hover:text-amber-50 text-xs disabled:opacity-60
                               disabled:cursor-not-allowed">
                        {{ netbird.checking ? 'Checking…' : 'Check again' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Global "not connected to NetBird" banner. Same red treatment as
             the in-page error rows (Login required etc.), but full-width and
             with a retry affordance so users can re-probe after starting the
             NetBird client without reloading the page. -->
        <div v-if="showNetbirdWarning && !lnaBlocked"
             class="bg-red-900/40 border-b border-red-700 text-red-200 px-4 sm:px-6 py-3">
            <div class="max-w-7xl mx-auto flex items-start gap-3 text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round"
                     stroke-linejoin="round" class="w-5 h-5 flex-shrink-0 mt-0.5 text-red-300">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <div class="flex-1 min-w-0">
                    <p class="font-medium text-red-100">Not connected to NetBird</p>
                    <p class="mt-0.5 text-red-300">
                        You can browse this list, but RDP shortcuts and admin links won't reach your sites.
                        Start the NetBird client, then retry — or use
                        <RouterLink to="/connect" class="underline hover:text-red-100">Connect</RouterLink>
                        if you haven't enrolled this device yet.
                    </p>
                </div>
                <button
                    type="button"
                    :disabled="netbird.checking"
                    @click="netbird.probe()"
                    class="flex-shrink-0 px-3 py-1 rounded border border-red-700 text-red-200
                           hover:bg-red-900/40 hover:text-red-100 text-xs disabled:opacity-60
                           disabled:cursor-not-allowed">
                    {{ netbird.checking ? 'Checking…' : 'Retry' }}
                </button>
            </div>
        </div>

        <main class="flex-1">
            <RouterView />
        </main>
    </div>
</template>
